'use client'
import { useCallback } from 'react'
import { CalendarHeader } from './CalendarHeader'
import { MonthGrid } from './MonthGrid'
import { buildMonthGrid, dayState, monthName, type DayAvailability } from './_lib/bookingCalendarModel'
import { CallToBook } from './CallToBook'
import type { SessionMonthDay } from './_lib/sessionSlotModel'

type Props = {
  /** Visible month, 'YYYY-MM-01'. Sessions show ONE month at all breakpoints. */
  monthStart: string
  /** Per-day open/total slot counts keyed by month-start. Parent loads the visible month. */
  daysByMonth: Record<string, SessionMonthDay[]>
  /** The single selected local date, or null. */
  selected: string | null
  /** Inclusive 'YYYY-MM-DD' floor (today). Past days disabled; pagers clamp to the 12-month window. */
  minDate: string
  onDateChange: (date: string | null) => void
  onMonthChange: (monthStart: string) => void
  loading?: boolean
  /** Guidance line under the grid (replaced by the empty-month notice when the month has no open days). */
  hint: string
  /** Public tenant phone. When the month is empty, offer a call instead of a shrug. */
  phone?: string | null
}

/**
 * Session-mode calendar: ONE month (a session books a single occurrence — the
 * dual-month layout exists for daily cross-month ranges and is dead weight
 * here), the shared CalendarHeader dropdowns, and MonthGrid's color-coded
 * cells. Days with zero open slots render sold-out; tapping a day hands off
 * to the slot list below the calendar.
 */
export function SessionCalendar({
  monthStart, daysByMonth, selected, minDate, onDateChange, onMonthChange, loading, hint, phone,
}: Props) {
  const pick = useCallback((iso: string) => {
    if (iso < minDate) return // past day — never selectable
    onDateChange(iso === selected ? null : iso)
  }, [selected, minDate, onDateChange])

  // MonthGrid's cells key on `remaining` — for sessions that's the day's open
  // occurrence count, so 0-open days disable exactly like sold-out daily days.
  const toDays = (days: SessionMonthDay[] | undefined): DayAvailability[] =>
    (days ?? []).map(d => ({ date: d.date, remaining: d.openSlots }))

  const model = buildMonthGrid(monthStart, toDays(daysByMonth[monthStart]))
  // Single-date selection rendered through MonthGrid's range model: start ===
  // end === the picked day, so the cell shows as an endpoint with no span.
  const gridSelected = { start: selected, end: selected }
  const empty =
    !loading &&
    daysByMonth[monthStart] !== undefined &&
    !model.cells.some(c => dayState(c.iso, c.remaining, 1, minDate) === 'available')

  return (
    <div>
      <CalendarHeader monthStart={monthStart} minDate={minDate} onMonthChange={onMonthChange} />
      <MonthGrid
        model={model} qty={1} selected={gridSelected} hover={null} minDate={minDate}
        onPick={pick} onHover={() => {}} loading={loading} showLabel={false}
      />
      <div className="mt-3 border-t border-[var(--color-border)] pt-3">
        {empty && phone ? (
          <CallToBook
            phone={phone}
            message={`No availability in ${monthName(monthStart)} — try another month, or ask us what's open.`}
          />
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">
            {empty ? `No availability in ${monthName(monthStart)} — try another month.` : hint}
          </p>
        )}
      </div>
    </div>
  )
}
